import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { UtilsService } from '@fantaz/shared/data-access/api';
import {
  BestScores,
  CasualTournamentDetails,
  columnTypes,
  CurrentUser,
  EventSchedule,
  IColumnModel,
  ITableConfig,
  Leader,
  PrizeDetails,
} from '@fantaz/shared/data-access/models';
import { CurrencyModule } from '@fantaz/shared/ui/currency';
import { GamesUiGameDetailsModule } from '@fantaz/shared/ui/game-details';
import { SharedUiTableComponentModule } from '@fantaz/shared/ui/table-component';
import { TournamentsUiScoreSummaryModule } from '@fantaz/tournaments/ui/score-summary';

@Component({
  selector: 'fz-tab-leaderboard',
  standalone: true,
  imports: [
    TournamentsUiScoreSummaryModule,
    MatCardModule,
    CurrencyModule,
    MatIconModule,
    GamesUiGameDetailsModule,
    SharedUiTableComponentModule,
  ],
  templateUrl: './tournament-leaderboard.html',
  styleUrls: ['./tournament-leaderboard.scss'],
})
export class TournamentLeaderboard implements OnInit, OnChanges {
  @Input() leaders: Leader[] | null = [];
  @Input() eventSchedulePrizes: Map<number, PrizeDetails> | null;
  @Input() loading: boolean | null;
  @Input() eventCode?: string;
  @Input() casualTournamentDetails?: CasualTournamentDetails | null;
  @Input() loaded: boolean | null;
  @Input() bestScores?: BestScores | undefined;
  @Input() seats?: number | undefined;
  @Input() prizeImage: string | undefined;
  @Input() isLEP: boolean | undefined;
  @Input() prizeValue?: number | null;
  @Input() entryAmount?: number;
  @Input() user: CurrentUser | null | undefined;
  @Input() retired: boolean | undefined;
  @Input() showScores: boolean | undefined;
  @Input() isLoggedIn: boolean;
  @Input() eventSchedule: EventSchedule | undefined;
  @Input() eventPrototypeUuid: string | undefined;
  @Input() casualGameStartTime: string | undefined;
  @Input() casualGameEndTime: string | undefined;
  @Input() playControls: TemplateRef<any>;

  @Output() share: EventEmitter<void> = new EventEmitter();
  @Output() playGame: EventEmitter<void> = new EventEmitter();
  @Output() loginEvent: EventEmitter<void> = new EventEmitter();
  @Output() tryForFree: EventEmitter<void> = new EventEmitter();
  @Output() leaderboardPageChange: EventEmitter<{
    pageIndex: number;
    pageSize: number;
  }> = new EventEmitter();

  displayedColumns: IColumnModel[] = [
    {
      id: 'rank',
      title: 'Place',
      type: () => columnTypes.CUSTOM,
      mapping: (element: Leader) => element.rank,
      tooltip: () => null,
      customElement: (l: Leader) =>
        this.utilsService.getPlaceCustomElement(
          l,
          this.eventSchedulePrizes,
          !!this.showScores,
          !!this.isLEP,
        ),
      showCustomPopover: (l: Leader): boolean =>
        !!this.eventSchedulePrizes?.get(Number(l.rank)) && !!this.showScores,
      customPopover: (l: Leader) =>
        this.utilsService.getPlaceCustomPopover(
          l,
          this.eventSchedulePrizes,
          !!this.isLEP,
        ),
    },
    {
      id: 'player',
      title: 'Player',
      srcImg: '/shared/svg/fantaz_icon_employee_badge.svg',
      type: (element: Leader) => {
        if (element.isFantazEmployee) {
          return columnTypes.CHIP;
        }
        return columnTypes.DEFAULT;
      },
      mapping: (element: Leader) => element.player,
      tooltip: (element: Leader) => {
        if (element.isFantazEmployee) {
          return `FantaZ employees use the Services and/or Software to test the user experience to be sure our platform is incredible, but they can't withdraw money. As a result, you may see a member of the FantaZ team playing your favorite Tournaments with you, and you will be able to identify them by their FantaZ badge.
            FantaZ directors, investors, contractors, affiliates, or partners may use the Website, Services and/or Software without such limitation, but only if they do not have any access to non-public information relating to the Services and/or Software that would lead to any advantage in their play using the Services and/or Software.`;
        }
        return null;
      },
    },
    {
      id: 'score',
      title: 'Score',
      type: () => columnTypes.DEFAULT,
      mapping: (element: any) => element.score,
      tooltip: () => null,
    },
    {
      id: 'lastTime',
      title: 'Last Played',
      type: () => columnTypes.DATE,
      mapping: (element: any) => element.lastTimePlayed,
      tooltip: () => null,
    },
    {
      id: 'timesPlayed',
      title: 'Entries',
      type: () => columnTypes.DEFAULT,
      mapping: (element: any) => element.timesPlayed,
      tooltip: () => null,
    },
  ];
  configuration: ITableConfig = {
    fixedLayout: true,
    tableName: 'leaderboard',
    pagination: {
      showPagination: true,
      paginationTotal: 0,
      sizeQueryParam: 'pageSize',
      indexQueryParam: 'pageIndex',
      paginationSizeOptions: [10, 50, 100],
    },
    emptyState: {
      title: ' ',
      loggedOutMessage:
        'Great moment to break the ice! Log in to join the competition!',
      loggedInMessage:
        'Great moment to break the ice! Dive in and join the competition now!',
    },
  };
  @Input()
  page: {
    pageIndex: number;
    pageSize: number;
  };
  @Input()
  total: number | null;

  constructor(private utilsService: UtilsService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['total'] && this.configuration.pagination) {
      this.configuration.pagination.paginationTotal = this.total;
      this.configuration.pagination.showPagination = Number(this.total) > 10;
    }
  }
  ngOnInit(): void {
    if (!this.showScores) {
      this.displayedColumns.splice(2, 1);
    }
  }

  onShare() {
    this.share.emit();
  }
  onPlay() {
    this.playGame.emit();
  }
  onTryForFree() {
    this.tryForFree.emit();
  }

  onLogin() {
    this.loginEvent.emit();
  }
  inTheFuture() {
    if (this.casualGameStartTime) {
      return (
        new Date(this.casualGameStartTime).getTime() > new Date().getTime()
      );
    }
    return false;
  }

  onLeaderboardPageChange(
    pageInfo: { pageIndex: number; pageSize: number } | any,
  ) {
    this.page = pageInfo;
    this.leaderboardPageChange.emit(pageInfo);
  }
}
